/**
 * Controlled modal for creating/editing one coupon. Client-side validation mirrors the
 * createCoupon/updateCoupon Zod contracts. On submit it calls onSave(values); the parent
 * owns the mutation so this stays presentational. Esc / backdrop close; focus trapped.
 *
 * Reuses the shared `.modal*` chrome from PriceEditModal. In edit mode `code` is read-only —
 * updateCoupon cannot change the code (uniqueness is on lower(code)+channel).
 */
import { useEffect, useId, useRef, useState } from 'react';
import '../PriceEditModal/priceEditModal.css';

export type Channel = 'amazon' | 'both';
export type DiscountType = 'percent' | 'fixed_amount';

export interface CouponFormValues {
  code: string; // ignored by the parent in edit mode
  description: string | null;
  discountType: DiscountType;
  discountValue: number;
  channel: Channel;
  startsAt: string | null; // ISO 8601 or null
  endsAt: string | null;
}

export interface CouponInitial {
  code: string;
  description?: string | null;
  discount_type?: DiscountType;
  discount_value?: number | string | null; // MCP sends numeric as a string
  channel?: Channel;
  starts_at?: string | null;
  ends_at?: string | null;
}

interface Props {
  mode: 'create' | 'edit';
  initial?: CouponInitial;
  saving?: boolean;
  onSave: (values: CouponFormValues) => void;
  onClose: () => void;
}

const pad = (n: number) => String(n).padStart(2, '0');
/** ISO -> value for <input type="datetime-local"> (local time, no seconds). */
const toLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
/** datetime-local value -> ISO (or null if blank/invalid). */
const toIso = (local: string): string | null => {
  if (local.trim() === '') return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

export function CouponEditModal({ mode, initial, saving = false, onSave, onClose }: Props) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [discountType, setDiscountType] = useState<DiscountType>(initial?.discount_type ?? 'percent');
  const [discountValue, setDiscountValue] = useState(
    initial?.discount_value != null && initial.discount_value !== '' ? String(Number(initial.discount_value)) : '',
  );
  const [channel, setChannel] = useState<Channel>(initial?.channel ?? 'both');
  const [startsAt, setStartsAt] = useState(toLocalInput(initial?.starts_at));
  const [endsAt, setEndsAt] = useState(toLocalInput(initial?.ends_at));
  const [error, setError] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const titleId = useId();

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    firstFieldRef.current?.focus();
    return () => opener?.focus?.();
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const f = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (f.length === 0) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mode === 'create' && code.trim() === '') {
      setError('Enter a coupon code.');
      return;
    }
    const val = Number(discountValue);
    if (discountValue.trim() === '' || !Number.isFinite(val) || val < 0) {
      setError('Enter a discount value (a number ≥ 0).');
      return;
    }
    if (discountType === 'percent' && val > 100) {
      setError('A percent discount cannot exceed 100.');
      return;
    }
    const startIso = toIso(startsAt);
    const endIso = toIso(endsAt);
    if (startIso && endIso && new Date(endIso) < new Date(startIso)) {
      setError('End must be on or after start.');
      return;
    }
    setError(null);
    onSave({
      code: code.trim(),
      description: description.trim() === '' ? null : description.trim(),
      discountType,
      discountValue: val,
      channel,
      startsAt: startIso,
      endsAt: endIso,
    });
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="modal__title">
          {mode === 'create' ? 'New coupon' : <>Edit coupon — <span className="modal__sku">{initial?.code}</span></>}
        </h2>

        <form onSubmit={handleSubmit} className="modal__form">
          {mode === 'create' && (
            <label className="modal__field">
              <span className="modal__label">Code</span>
              <input
                ref={firstFieldRef}
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="modal__input"
                maxLength={64}
                required
              />
            </label>
          )}

          <label className="modal__field">
            <span className="modal__label">Channel</span>
            <select className="modal__input" value={channel} onChange={(e) => setChannel(e.target.value as Channel)}>
              <option value="amazon">Amazon</option>
              <option value="both">Both</option>
            </select>
          </label>

          <label className="modal__field">
            <span className="modal__label">Discount type</span>
            <select className="modal__input" value={discountType} onChange={(e) => setDiscountType(e.target.value as DiscountType)}>
              <option value="percent">Percent (%)</option>
              <option value="fixed_amount">Fixed amount ($)</option>
            </select>
          </label>

          <label className="modal__field">
            <span className="modal__label">Discount value {discountType === 'percent' ? '(%)' : '(USD)'}</span>
            <input
              ref={mode === 'edit' ? firstFieldRef : undefined}
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              max={discountType === 'percent' ? '100' : undefined}
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              className="modal__input"
              required
            />
          </label>

          <label className="modal__field">
            <span className="modal__label">
              Description <span className="modal__optional">— optional</span>
            </span>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="modal__input"
              maxLength={500}
            />
          </label>

          <label className="modal__field">
            <span className="modal__label">
              Starts <span className="modal__optional">— optional</span>
            </span>
            <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className="modal__input" />
          </label>

          <label className="modal__field">
            <span className="modal__label">
              Ends <span className="modal__optional">— optional</span>
            </span>
            <input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className="modal__input" />
          </label>

          {error && (
            <p className="modal__error" role="alert">
              {error}
            </p>
          )}

          <div className="modal__actions">
            <button type="button" className="modal__btn modal__btn--ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="modal__btn modal__btn--primary" disabled={saving}>
              {saving ? 'Saving…' : mode === 'create' ? 'Create coupon' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
